import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  billing_cycle: string;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  plan_name: string;
  plan_description: string;
  plan_billing_cycle: string;
  plan_features: string[];
  status: string;
  subscription_start_date: string;
  subscription_end_date: string;
  trial_start_date: string;
  trial_end_date: string;
  plan_price: number;
  created_at: string;
  updated_at: string;
}

export const useSubscriptions = () => {
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    SubscriptionPlan[]
  >([]);
  const [userSubscriptions, setUserSubscriptions] = useState<
    UserSubscription[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubscriptionPlans = async () => {
    try {
      console.log(
        "Fetching available subscription plans from subscriptions table..."
      );

      // Get unique plan combinations from subscriptions table
      const { data, error } = await supabase
        .from("subscriptions")
        .select(
          "plan_name, plan_description, plan_price, plan_billing_cycle, plan_features"
        )
        .not("plan_name", "is", null);

      if (error) throw error;

      // Create unique plans from subscription data
      const uniquePlansMap = new Map();

      data?.forEach((sub) => {
        if (sub.plan_name && !uniquePlansMap.has(sub.plan_name)) {
          uniquePlansMap.set(sub.plan_name, {
            id: sub.plan_name.toLowerCase().replace(/\s+/g, "-"),
            name: sub.plan_name,
            description: sub.plan_description || "",
            price: Number(sub.plan_price || 0),
            billing_cycle: sub.plan_billing_cycle || "monthly",
            features: (() => {
              if (!sub.plan_features) return [];
              if (typeof sub.plan_features === "string") {
                try {
                  return JSON.parse(sub.plan_features);
                } catch {
                  return [];
                }
              }
              if (Array.isArray(sub.plan_features)) {
                return sub.plan_features as string[];
              }
              return [];
            })(),
            is_active: true,
            sort_order:
              sub.plan_name === "Basic Plan"
                ? 1
                : sub.plan_name === "Premium Plan"
                ? 2
                : 3,
          });
        }
      });

      const transformedData = Array.from(uniquePlansMap.values()).sort(
        (a, b) => a.sort_order - b.sort_order
      );

      console.log("Available subscription plans:", transformedData);
      setSubscriptionPlans(transformedData);
    } catch (error: any) {
      console.error("Error fetching subscription plans:", error);
      toast({
        title: "Error",
        description: "Failed to load subscription plans",
        variant: "destructive",
      });
    }
  };

  const fetchUserSubscriptions = async () => {
    try {
      console.log("=== FETCHING USER SUBSCRIPTIONS ===");

      // Fetch subscriptions
      const { data: subscriptionsData, error: subscriptionsError } =
        await supabase
          .from("subscriptions")
          .select("*")
          .order("created_at", { ascending: false });

      if (subscriptionsError) throw subscriptionsError;

      console.log("Raw subscriptions data:", subscriptionsData);

      // Fetch users with auth_id included
      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, auth_id, profile");

      if (usersError) throw usersError;

      console.log("Raw users data:", usersData);

      // Create lookup map using auth_id as key
      const usersMap = new Map(usersData.map((user) => [user.auth_id, user]));
      console.log("Users map created with auth_id as key:", usersMap);

      const transformedData: UserSubscription[] = subscriptionsData.map(
        (sub) => {
          const user = usersMap.get(sub.user_id);
          const profile = user?.profile || {};

          // Log the comparison for each subscription
          console.log(`Processing subscription ID: ${sub.id}`);
          console.log(`  - Looking for user_id: ${sub.user_id}`);
          console.log(
            `  - Found user:`,
            user ? `Yes (ID: ${user.id}, auth_id: ${user.auth_id})` : "No"
          );

          // Safely extract profile data
          let firstName = "";
          let lastName = "";

          if (profile && typeof profile === "object" && profile !== null) {
            const profileObj = profile as Record<string, any>;
            firstName = profileObj.firstName || profileObj.first_name || "";
            lastName = profileObj.lastName || profileObj.last_name || "";
          }

          // Parse features safely
          let features: string[] = [];
          if (sub.plan_features) {
            if (typeof sub.plan_features === "string") {
              try {
                const parsed = JSON.parse(sub.plan_features);
                features = Array.isArray(parsed)
                  ? parsed.filter((item) => typeof item === "string")
                  : [];
              } catch {
                features = [];
              }
            } else if (Array.isArray(sub.plan_features)) {
              features = sub.plan_features.filter(
                (item) => typeof item === "string"
              ) as string[];
            }
          }

          return {
            id: sub.id,
            user_id: sub.user_id,
            user_first_name: firstName || "Unknown",
            user_last_name: lastName ,
            plan_name: sub.plan_name || "",
            plan_description: sub.plan_description || "",
            plan_billing_cycle: sub.plan_billing_cycle || "monthly",
            plan_features: features,
            status: sub.status,
            subscription_start_date: sub.subscription_start_date || "",
            subscription_end_date: sub.subscription_end_date || "",
            trial_start_date: sub.trial_start_date || "",
            trial_end_date: sub.trial_end_date || "",
            plan_price: Number(sub.plan_price || 0),
            created_at: sub.created_at,
            updated_at: sub.updated_at,
          };
        }
      );

      // Log final comparison summary
      console.log("=== FINAL COMPARISON SUMMARY ===");
      console.log(`Total subscriptions: ${subscriptionsData.length}`);
      console.log(`Total users: ${usersData.length}`);
      console.log(
        `Matched subscriptions: ${
          transformedData.filter((sub) =>
            usersData.some((user) => user.auth_id === sub.user_id)
          ).length
        }`
      );
      console.log(
        `Unmatched subscriptions: ${
          transformedData.filter(
            (sub) => !usersData.some((user) => user.auth_id === sub.user_id)
          ).length
        }`
      );

      console.log("=== TRANSFORMED SUBSCRIPTIONS DATA ===", transformedData);
      setUserSubscriptions(transformedData);
    } catch (error: any) {
      console.error("Error fetching user subscriptions:", error);
      toast({
        title: "Error",
        description: "Failed to load subscriptions",
        variant: "destructive",
      });
    }
  };

  const calculateMonthlyRevenue = () => {
    console.log("=== CALCULATING MONTHLY REVENUE ===");
    console.log(
      "All subscriptions for revenue calculation:",
      userSubscriptions
    );

    // Filter for revenue-generating subscriptions:
    // 1. Status must be 'active' (not trialing, canceled, expired, etc.)
    // 2. Must have a valid subscription end date that hasn't passed
    // 3. Must have a plan price greater than 0
    const revenueGeneratingSubscriptions = userSubscriptions.filter((sub) => {
      const isActive = sub.status === "active";
      const hasValidEndDate =
        sub.subscription_end_date &&
        new Date(sub.subscription_end_date) > new Date();
      const hasPriceGreaterThanZero = sub.plan_price > 0;

      console.log(`Subscription ${sub.id} (${sub.plan_name}):`);
      console.log(`- Status: ${sub.status} (isActive: ${isActive})`);
      console.log(
        `- End date: ${sub.subscription_end_date} (hasValidEndDate: ${hasValidEndDate})`
      );
      console.log(
        `- Plan price: ${sub.plan_price} (hasPriceGreaterThanZero: ${hasPriceGreaterThanZero})`
      );
      console.log(
        `- Qualifies for revenue: ${
          isActive && hasValidEndDate && hasPriceGreaterThanZero
        }`
      );

      return isActive && hasValidEndDate && hasPriceGreaterThanZero;
    });

    console.log(
      "Revenue-generating subscriptions:",
      revenueGeneratingSubscriptions
    );

    const monthlyRevenue = revenueGeneratingSubscriptions.reduce(
      (total, sub) => {
        console.log(
          `Adding ${sub.plan_price} from ${sub.plan_name} subscription`
        );
        return total + sub.plan_price;
      },
      0
    );

    console.log("=== FINAL MONTHLY REVENUE ===");
    console.log(`Total monthly revenue: $${monthlyRevenue}`);
    console.log(
      `From ${revenueGeneratingSubscriptions.length} active subscriptions`
    );

    return monthlyRevenue;
  };

  const refetch = async () => {
    setLoading(true);
    await Promise.all([fetchSubscriptionPlans(), fetchUserSubscriptions()]);
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      console.log("Initial data fetch starting...");
      setLoading(true);
      await Promise.all([fetchSubscriptionPlans(), fetchUserSubscriptions()]);
      setLoading(false);
      console.log("Initial data fetch completed");
    };

    fetchData();
  }, []);

  const monthlyRevenue = calculateMonthlyRevenue();

  return {
    subscriptionPlans,
    userSubscriptions,
    monthlyRevenue,
    loading,
    refetch,
  };
};
